/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { MaterialSpec, TableInfoSpec, WallSpec } from "../../../shared/contracts";
import { type IPhysicalData, ItemData, solidOutline, toDragPoints } from "../../core/item-data";
import { resolvePhysics } from "../../core/material";
import type { DragPoint } from "../../math/dragpoint";

export class SurfaceData extends ItemData implements IPhysicalData {
  public hitEvent: boolean = false;
  public isDroppable: boolean = false;
  public isBottomSolid: boolean = false;
  public isCollidable: boolean = true;
  public threshold: number = 2.0;
  public szPhysicsMaterial?: string;
  public szSurface?: string;
  public heightBottom: number = 0;
  public heightTop: number = 50;
  public slingshotForce: number = 80;
  public slingshotThreshold: number = 0;
  public elasticity: number = 0.3;
  public elasticityFalloff: number = 0;
  public friction: number = 0.3;
  public scatter: number = 0;
  /** The spec values are final, so the material does not overwrite them. */
  public overwritePhysics: boolean = true;
  public isTopBottomVisible: boolean = true;
  public isSideVisible: boolean = true;
  public dragPoints: DragPoint[] = [];

  // non-persisted
  public isDisabled = false;

  public constructor(itemName: string) {
    super(itemName);
  }

  /**
   * Builds the data from a table spec.
   * Physics values that the spec leaves out come from the material, then from the table.
   */
  public static fromSpec(
    spec: WallSpec,
    info: TableInfoSpec,
    materials?: ReadonlyMap<string, MaterialSpec>,
  ): SurfaceData {
    const data = new SurfaceData(spec.name);
    const phys = resolvePhysics(spec, info, materials);
    data.hitEvent = spec.hitEvent ?? data.hitEvent;
    data.isDroppable = spec.isDroppable ?? data.isDroppable;
    data.isBottomSolid = spec.isBottomSolid ?? data.isBottomSolid;
    data.isCollidable = spec.isCollidable ?? true;
    data.threshold = phys.threshold ?? data.threshold;
    data.szPhysicsMaterial = spec.szMaterial;
    data.szSurface = spec.szSurface;
    data.heightBottom = spec.heightBottom ?? data.heightBottom;
    data.heightTop = spec.heightTop;
    data.slingshotForce = spec.slingshotForce ?? data.slingshotForce;
    data.slingshotThreshold = spec.slingshotThreshold ?? data.slingshotThreshold;
    data.elasticity = phys.elasticity;
    data.elasticityFalloff = phys.elasticityFalloff;
    data.friction = phys.friction;
    data.scatter = phys.scatter;
    data.isTopBottomVisible = spec.isTopBottomVisible ?? spec.isVisible ?? true;
    data.isSideVisible = spec.isSideVisible ?? spec.isVisible ?? true;
    data.dragPoints = solidOutline(toDragPoints(spec.dragPoints));
    return data;
  }
}
